import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon, RotateCcwIcon } from 'lucide-react';

/**
 * Approved revision-06 vignette (Higgsfield preview hosting).
 * Replace these with self-hosted assets before production.
 * Rendered as the background of the combined footer — the footer's own
 * foreground content sits above it.
 */
const DESKTOP_MP4 = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/6b8ffb3e-579e-405e-93df-8a1cf2b06e95.mp4';
const MOBILE_MP4 = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/38d1e55a-0252-4c4d-b7d9-9fe64ec6c9ad.mp4';
const POSTER = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/3e58413b-9028-4e66-aa78-58c5041ce129.webp';
const VISIBILITY_THRESHOLD = 0.35;
type Status = 'idle' | 'playing' | 'paused' | 'ended' | 'failed';
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & {
    connection?: {
      saveData?: boolean;
    };
  }).connection;
  return Boolean(connection?.saveData);
}
export function FooterCatVignette() {
  const layerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPausedRef = useRef(false);
  const visibleRef = useRef(false);

  // Source is chosen once, at mount, from the initial viewport width.
  const [source] = useState<string>(() => typeof window !== 'undefined' && window.innerWidth <= 767 ? MOBILE_MP4 : DESKTOP_MP4);
  const [staticOnly, setStaticOnly] = useState<boolean>(() => prefersReducedMotion() || prefersSaveData());
  const [shouldLoad, setShouldLoad] = useState(false);
  const [status, setStatus] = useState<Status>('idle');

  // React to preference changes.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setStaticOnly(query.matches || prefersSaveData());
    if (query.addEventListener) query.addEventListener('change', onChange);else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);else query.removeListener(onChange);
    };
  }, []);
  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || staticOnly) return;
    if (userPausedRef.current) return;
    if (video.ended) return;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.then(() => setStatus('playing')).catch(() => setStatus(current => current === 'ended' ? current : 'idle'));
    }
  }, [staticOnly]);

  // Lazy-load the source when the footer gets close.
  useEffect(() => {
    const node = layerRef.current;
    if (!node || staticOnly || shouldLoad) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '300px 0px'
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [staticOnly, shouldLoad]);

  // Play / pause on the 35% visibility threshold of the combined footer.
  useEffect(() => {
    const node = layerRef.current;
    if (!node || staticOnly || !shouldLoad) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;
      visibleRef.current = visible;
      const video = videoRef.current;
      if (!video) return;
      if (visible) {
        attemptPlay();
      } else if (!video.paused) {
        video.pause();
        setStatus(current => current === 'ended' ? current : 'paused');
      }
    }, {
      threshold: [0, VISIBILITY_THRESHOLD, 0.6]
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [staticOnly, shouldLoad, attemptPlay]);

  // Pause with the document.
  useEffect(() => {
    if (staticOnly) return;
    const onVisibilityChange = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        if (!video.paused) {
          video.pause();
          setStatus(current => current === 'ended' ? current : 'paused');
        }
      } else if (visibleRef.current) {
        attemptPlay();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [staticOnly, attemptPlay]);
  const handleToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (status === 'ended') {
      userPausedRef.current = false;
      video.currentTime = 0;
      setStatus('playing');
      void video.play().catch(() => setStatus('idle'));
      return;
    }
    if (!video.paused) {
      userPausedRef.current = true;
      video.pause();
      setStatus('paused');
      return;
    }
    userPausedRef.current = false;
    void video.play().then(() => setStatus('playing')).catch(() => setStatus('idle'));
  };
  const controlLabel = status === 'ended' ? 'Replay cat animation' : status === 'playing' ? 'Pause cat animation' : status === 'paused' ? 'Resume cat animation' : 'Play cat animation';
  const ControlIcon = status === 'playing' ? PauseIcon : status === 'ended' ? RotateCcwIcon : PlayIcon;
  return <>
      <div ref={layerRef} className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-33" data-mp="2a45kt2bkd4wu">
        {/* On phones the whole scene is scaled down and anchored to the bottom
            (contain) so the cat and the glass both stay complete; the top edge
            is feathered into the cream footer. Desktop keeps its full-bleed crop. */}
        <div className="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent_0%,#000_18%,#000_100%)] md:[mask-image:none]" data-id="element-34" data-mp="2vz4gzymvcr4t">
          <img src={POSTER} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-contain object-bottom md:object-cover md:object-[center_bottom]" data-id="element-35" data-mp="30z8myuq5i3gs" />

          {!staticOnly && shouldLoad && status !== 'failed' ? <video ref={videoRef} src={source} poster={POSTER} muted playsInline preload="auto" aria-hidden="true" tabIndex={-1} onPlay={() => setStatus('playing')} onPause={() => {
          const video = videoRef.current;
          if (video && !video.ended) {
            setStatus('paused');
          }
        }} onEnded={() => setStatus('ended')} onError={() => setStatus('failed')} className="absolute inset-0 h-full w-full object-contain object-bottom md:object-cover md:object-[center_bottom]" data-id="element-36" data-mp="1fkmfdbildv29" /> : null}
        </div>

        {/* Warm Cream veil: strongest where the copy sits, light over the cat. */}
        <div className="absolute inset-0 md:hidden" style={{
        background: 'linear-gradient(to bottom, rgba(245,240,232,0.9) 0%, rgba(245,240,232,0.86) 55%, rgba(245,240,232,0.55) 78%, rgba(245,240,232,0.4) 90%, rgba(245,240,232,0.62) 100%)'
      }} data-id="element-37" data-mp="1ly8i5jlmw3xh" />
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(to bottom, rgba(245,240,232,0.95) 0%, rgba(245,240,232,0.88) 38%, rgba(245,240,232,0.6) 62%, rgba(245,240,232,0.44) 84%, rgba(245,240,232,0.72) 100%)'
      }} data-id="element-38" data-mp="5nxo319bzyie" />
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(to right, rgba(245,240,232,0.72) 0%, rgba(245,240,232,0.34) 42%, rgba(245,240,232,0) 70%)'
      }} data-id="element-39" data-mp="qaw0we46p5wd" />
      </div>

      {!staticOnly && status !== 'failed' ? <button type="button" onClick={handleToggle} title={controlLabel} className="absolute bottom-5 right-5 z-20 inline-flex h-11 w-11 items-center justify-center rounded-[2px] border border-ink/25 bg-cream/70 text-ink transition-colors duration-150 ease-out hover:border-ink/50 hover:bg-cream md:bottom-6 md:right-8 lg:right-10" data-id="element-40" data-mp="158oe2zm9di7e">
          <ControlIcon className="h-4 w-4" aria-hidden="true" data-id="element-41" data-mp="3mgg61g219af0" />
          <span className="sr-only" data-id="element-42" data-mp="3men2i9mpv6b8">{controlLabel}</span>
        </button> : null}
    </>;
}